"use client";

import "flatpickr/dist/flatpickr.min.css";
import Flatpickr from "react-flatpickr";
import { Arabic } from "flatpickr/dist/l10n/ar.js";
import type { Instance } from "flatpickr/dist/types/instance";
import { useLocale } from "next-intl";
import { useIsMobile } from "@/hooks/use-mobile";

export type DateTimePickerMode = "date" | "datetime";

export type DateTimePickerPosition =
  | "auto"
  | "above"
  | "below"
  | "auto left"
  | "auto center"
  | "auto right"
  | "above left"
  | "above center"
  | "above right"
  | "below left"
  | "below center"
  | "below right"
  | ((self: Instance, customElement: HTMLElement | undefined) => void);

type BaseProps = {
  placeholder?: string;
  mode?: DateTimePickerMode;
  minDate?: Date | string | number;
  maxDate?: Date | string | number;
  disabled?: boolean;
  dir?: "rtl" | "ltr";
  /** Flatpickr popup position (e.g. "auto right", "auto center") */
  position?: DateTimePickerPosition;
  /**
   * Where to mount the calendar popup in the DOM.
   * Useful inside Radix dialogs when you want a popup calendar (not `static`)
   * but still need it to be interactive (avoid being treated as "outside" the dialog).
   */
  appendTo?: HTMLElement | null;
  /**
   * Render the calendar inline next to the input (instead of portaling to `body`).
   * Useful inside Radix dialogs where outside elements may have `pointer-events: none`.
   */
  staticCalendar?: boolean;
  className?: string;
};

type DateValueProps = BaseProps & {
  /** Controlled value as a Date (recommended) */
  value: Date | null;
  /** Receives the selected Date (or null when cleared) */
  onChange: (value: Date | null) => void;
};

type StringValueProps = BaseProps & {
  /**
   * Controlled value as a formatted string (legacy).
   * Format is controlled by `mode` (date: `Y-m-d`, datetime: `Y-m-d H:i`).
   */
  value: string;
  /** Receives the formatted date string (legacy) */
  onChange: (value: string) => void;
};

type Props = DateValueProps | StringValueProps;

export function DateTimePicker({
  value,
  onChange,
  placeholder,
  mode = "date",
  minDate,
  maxDate,
  disabled,
  dir,
  position,
  appendTo,
  staticCalendar = false,
  className,
}: Props) {
  const locale = useLocale();
  const isMobile = useIsMobile();
  const enableTime = mode === "datetime";
  const isStringValue = typeof value === "string";
  const resolvedDir: "rtl" | "ltr" = dir ?? (locale === "ar" ? "rtl" : "ltr");

  return (
    <Flatpickr
      value={value || undefined}
      options={{
        locale: locale === "ar" ? Arabic : undefined,
        // On touch devices typing is impractical and the virtual keyboard
        // covers the calendar, so the input is readonly there (picker only).
        allowInput: !isMobile,
        disableMobile: true,
        enableTime,
        static: staticCalendar,
        appendTo: appendTo ?? undefined,
        time_24hr: false,
        minuteIncrement: 1,
        // Match ASP.NET-style UX: dropdown month/year and consistent popup placement
        monthSelectorType: "dropdown",
        // Use auto positioning so the calendar doesn't overflow the viewport/modal
        position:
          position ?? (resolvedDir === "rtl" ? "auto right" : "auto left"),
        dateFormat: enableTime ? "Y-m-d H:i" : "Y-m-d",
        minDate,
        maxDate,
        // In static mode the calendar is in the document flow inside a
        // scrollable modal body; bring it into view when it opens.
        onOpen: staticCalendar
          ? [
              (_dates, _str, inst) => {
                requestAnimationFrame(() => {
                  inst.calendarContainer?.scrollIntoView({
                    block: "nearest",
                    behavior: "smooth",
                  });
                });
              },
            ]
          : undefined,
      }}
      onChange={(dates, dateStr) => {
        if (isStringValue) {
          (onChange as StringValueProps["onChange"])(dateStr);
          return;
        }
        (onChange as DateValueProps["onChange"])(dates?.[0] ?? null);
      }}
      render={(_props, ref) => (
        <div dir={resolvedDir} className="relative z-50">
          <input
            ref={ref as unknown as React.Ref<HTMLInputElement>}
            placeholder={placeholder}
            disabled={disabled}
            className={[
              "w-full h-14 rounded-xl border border-gray-100 bg-white px-4 text-start outline-none",
              "focus:ring-2 focus:ring-ring focus:ring-offset-0",
              disabled ? "opacity-60 cursor-not-allowed" : "",
              className || "",
            ].join(" ")}
          />
          <style jsx global>{`
            .flatpickr-calendar {
              position: absolute !important;
              pointer-events: auto !important;
              z-index: 9999 !important;
            }
          `}</style>
        </div>
      )}
    />
  );
}
